import { Link } from "@tanstack/react-router";
import { ArrowRight, Sparkles } from "lucide-react";

import { Button } from "@/components/button";
import { useGetAlphaDesignTypes } from "@/hooks/useGetAlphaDesignTypes";
import { useGetCurrentProjectId } from "@/hooks/useGetCurrentProjectId";

import { RECENT_PROJECTS, STEPS } from "./mockData";

function HeroIllustration() {
  return (
    <div
      aria-hidden
      className="relative hidden h-[410px] w-[450px] shrink-0 lg:block"
    >
      <div className="absolute left-0 top-[62px] flex h-[238px] w-[190px] -rotate-5 flex-col justify-between rounded-[6px] bg-[#1e3a34] p-4 shadow-[0_14px_30px_rgba(15,23,40,0.14)]">
        <span className="text-[8.7px] font-extrabold text-[#f6f1e7]/85">
          Bramble Pet Co.
        </span>
        <span className="text-[21px]/[1.1] font-extrabold text-[#f6f1e7]">
          Healthy tails, happy trails.
        </span>
        <span className="w-fit rounded-full bg-[#f2a65a] px-3 py-1 text-[10px] font-bold text-[#1e3a34]">
          Shop now
        </span>
      </div>

      <div className="absolute left-[243px] top-[86px] flex h-[231px] w-[185px] rotate-6 flex-col justify-between rounded-[6px] bg-[#f2efff] p-4 shadow-[0_14px_30px_rgba(15,23,40,0.14)]">
        <span className="text-[8.7px] font-extrabold text-[#231a4a]">
          Bramble Pet Co.
        </span>
        <div>
          <span className="block text-[40px]/[0.8] font-extrabold text-[#5b35e8]">
            “
          </span>
          <span className="text-[16.6px]/[1.2] font-bold text-[#231a4a]">
            Worth every penny. Our vet noticed too.
          </span>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-[13px] text-[#5b35e8]">★★★★★</span>
          <span className="text-[9px] font-bold text-[#231a4a]">
            Priya K., verified buyer
          </span>
        </div>
      </div>

      <div className="absolute left-[120px] top-[-4px] z-10 flex h-[269px] w-[215px] rotate-2 flex-col items-center justify-between rounded-[6px] bg-[#ffd84d] p-4 shadow-[0_18px_36px_rgba(15,23,40,0.18)]">
        <span className="self-start text-[8.7px] font-extrabold text-[#1b1b1b]">
          Bramble Pet Co.
        </span>
        <div className="flex flex-col items-center">
          <span className="text-[69px]/none font-extrabold tracking-tighter text-[#1b1b1b]">
            20%
          </span>
          <span className="text-[15px] font-extrabold uppercase tracking-[2px] text-[#1b1b1b]">
            OFF EVERYTHING
          </span>
        </div>
        <span className="rounded-full bg-[#1b1b1b] px-3 py-1 text-[10px] font-bold text-[#ffd84d]">
          Shop now
        </span>
      </div>
    </div>
  );
}

function Hero() {
  const projectId = useGetCurrentProjectId();
  const { data } = useGetAlphaDesignTypes();
  const designType = data?.designTypes[0]?.id;

  return (
    <section className="flex items-center justify-between gap-8">
      <div className="flex max-w-[560px] flex-col gap-[22px]">
        <span className="flex w-fit items-center gap-2 rounded-full bg-bg-brand-primary px-3.5 py-[7px] text-sm font-bold text-text-brand-primary">
          <Sparkles className="size-4" />
          AI Ad Studio
        </span>
        <h1 className="text-4xl/tight font-extrabold tracking-tight text-text-primary lg:text-[52px]/[54.6px] lg:tracking-[-1.3px]">
          Make social ads in minutes.
        </h1>
        <p className="text-[19px]/[28.5px] text-text-secondary">
          Tell us what you&apos;re promoting, add your assets, pick a look. You
          get 10 ad options back, ready to tweak and download.
        </p>
        <div className="flex flex-wrap items-center gap-[18px] pt-1.5">
          {designType === undefined ? (
            <Button isDisabled size="2xl">
              Start generating
              <ArrowRight />
            </Button>
          ) : (
            <Button asChild size="2xl">
              <Link
                search={{
                  designType,
                  "project-id": projectId,
                  "request-type": "graphic",
                }}
                to="/requests/mbc"
                viewTransition
              >
                Start generating
                <ArrowRight />
              </Link>
            </Button>
          )}
          <span className="text-[15px] text-text-tertiary">
            No design experience needed.
          </span>
        </div>
      </div>
      <HeroIllustration />
    </section>
  );
}

function Steps() {
  return (
    <section className="grid grid-cols-1 gap-5 md:grid-cols-3">
      {STEPS.map((step, index) => (
        <div
          className="flex flex-col gap-2.5 rounded-[18px] bg-bg-secondary px-6 py-[22px]"
          key={step.title}
        >
          <span className="flex size-[34px] items-center justify-center rounded-[10px] bg-bg-brand-solid text-base font-extrabold text-white">
            {index + 1}
          </span>
          <h3 className="text-lg font-bold text-text-primary">{step.title}</h3>
          <p className="text-[15px]/[22.5px] text-text-secondary">
            {step.body}
          </p>
        </div>
      ))}
    </section>
  );
}

function RecentProjects() {
  return (
    <section className="flex flex-col gap-[18px]">
      <div className="flex items-center justify-between">
        <h2 className="text-[22px] font-bold text-text-secondary">
          Your recent projects
        </h2>
        <span className="text-[15px] font-bold text-text-brand-primary">
          View all
        </span>
      </div>
      <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
        {RECENT_PROJECTS.map(({ meta, thumbnail, title }) => (
          <div
            className="flex items-center gap-4 rounded-[18px] border border-border-secondary p-3.5"
            key={title}
          >
            <div
              aria-hidden
              className="flex size-[84px] shrink-0 items-center justify-center rounded-lg border border-border-secondary text-2xl font-extrabold"
              style={{
                background: thumbnail.background,
                color: thumbnail.color,
              }}
            >
              {thumbnail.text}
            </div>
            <div className="flex min-w-0 flex-col items-start gap-1">
              <span className="text-base font-bold text-text-primary">
                {title}
              </span>
              <span className="text-sm text-text-tertiary">{meta}</span>
              <span className="rounded-full bg-bg-tertiary px-[9px] py-[3px] text-xs font-bold text-text-secondary">
                Downloaded
              </span>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function AiAdStudioPage() {
  return (
    <div className="flex flex-col gap-11 rounded-3xl [view-transition-name:alpha-card] border border-border-secondary bg-bg-primary px-11 pb-10 pt-9">
      <Hero />
      <Steps />
      <RecentProjects />
    </div>
  );
}
